/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useState, useContext, useEffect } from 'react';
import GraphQLContext from './GraphQLProvider';
import AuthContext from './AuthProvider';
import { getStorage, setStorage, relocate } from '../helpers/general';
import { railwayApi } from 'common/util';
import { useToast } from '@chakra-ui/react';

const WishlistContext = createContext();

const query = `
  query($productIds: [Int!] = [], $variantIds: [Int!] = [], $width: Int = 100) {
    site {
      products(first: 50, entityIds: $productIds) {
        edges {
          node {
            defaultImage {
              url(width: $width)
            }
            entityId
            name
            sku
            path
            productOptions{
              edges{
                node{
                  isVariantOption
                  displayName
                }
              }
            }
            variants(entityIds: $variantIds) {
              edges {
                node {
                  entityId
                  id
                  options{
                    edges{
                      node{
                        displayName
                        values{
                          edges{
                            node{
                              label
                              entityId
                            }
                          }
                        }
                      }
                    }
                  }
                  productOptions{
                    edges{
                      node{
                        displayName
                        entityId
                      }
                    }
                  }
                }
              }
            }
            prices {
              price {
                value
              }
              retailPrice {
                value
              }
              salePrice {
                value
              }
            }
          }
        }
      }
    }
  }
`;

const initialState = {
  sessionWishlistIds: [],
  sessionWishlistIdCount: 0,
  sessionWishlistData: [],
  fetchRetries: 5,
  userExistingWishlistName: []
};

initialState.sessionWishlistIds = JSON.parse(getStorage('sessionWishlistIds')) || [];

initialState.sessionWishlistIdCount = initialState.sessionWishlistIds.length;

export const WishlistProvider = ({ children }) => {
  const graphql = useContext(GraphQLContext);
  const graphqlQuery = graphql && graphql.query;
  const toast = useToast();
  const auth = useContext(AuthContext);
  const isLoggedIn = auth && auth.state.isLoggedIn;
  const [init, setInit] = useState(false);
  const [updatingId, setUpdatingId] = useState(false);
  const [sessionWishlistIds, setSessionWishlistIds] = useState(initialState.sessionWishlistIds);
  const [sessionWishlistIdCount, setSessionWishlistIdCount] = useState(
    initialState.sessionWishlistIdCount
  );
  const [sessionWishlistData, setSessionWishlistData] = useState(initialState.sessionWishlistData);
  const [fetchRetries, setFetchRetries] = useState(initialState.fetchRetries);
  const [existingWishlists, setExistingWishlists] = useState(initialState.userExistingWishlistName);

  const getSessionWishlist = width => {
    if (sessionWishlistIds.length > 0) {
      const productIds = [];
      const variantIds = [];
      sessionWishlistIds.map(ids => {
        productIds.push(ids[0]);
        if (ids[1] !== null) {
          variantIds.push(ids[1]);
        }

        return true;
      });

      graphqlQuery(query, { productIds, variantIds, width })
        .then(response => {
          if (response.data && response.data.site.products.edges.length > 0) {
            /* Extend on product data here if needed - for example, fetch additional data from a PIM and add it to the product object */
            setSessionWishlistData(response.data.site.products.edges);
            setFetchRetries(initialState.fetchRetries);
          } else {
            return false;
          }
        })
        .catch(error => {
          // console.log("fetch list error:", error);
          if (fetchRetries > 0) {
            // console.log(state.fetchRetries);
            setFetchRetries(fetchRetries - 1);
            setTimeout(() => getSessionWishlist(), 1000);
          }
        });
    } else {
      setSessionWishlistData(initialState.sessionWishlistData);
      setFetchRetries(initialState.fetchRetries);
    }
  };

  const saveSessionWishlistIds = data => {
    setSessionWishlistIds(data);
    setSessionWishlistIdCount(data.length);
    setStorage('sessionWishlistIds', JSON.stringify(data));
    setUpdatingId(false);
  };

  useEffect(() => {
    getSessionWishlist();
  }, [sessionWishlistIds, sessionWishlistIdCount]);

  const loadSessionWishlist = () => {
    getSessionWishlist();
  };

  useEffect(() => {
    if (!init) {
      loadSessionWishlist();
      setInit(true);
    }
  }, [init]);

  const existsInWishlist = (productId, variantId) => {
    const currentWishlist = sessionWishlistIds;

    let existing = false;
    let existingIndex = null;

    currentWishlist.map((ids, idsIndex) => {
      if (ids[0] === productId && ids[1] === variantId) {
        existing = true;
        existingIndex = idsIndex;
      }

      return true;
    });

    return { active: existing, index: existingIndex };
  };

  const updateSessionWishlist = (productId, variantId) => {
    setUpdatingId({ productId, variantId });
    const currentWishlist = sessionWishlistIds;
    const existing = existsInWishlist(productId, variantId);

    if (existing.active) {
      currentWishlist.splice(existing.index, 1);
    } else {
      currentWishlist.push([productId, variantId]);
    }

    saveSessionWishlistIds(currentWishlist);
  };

  const clearSessionWishlist = () => {
    setSessionWishlistData([]);
    saveSessionWishlistIds([]);
  };

  const saveWishlist = (name, isLoggedInOverride, customerIdOverride) => {
    return new Promise(res => {
      // Check if user is logged in
      let logged = isLoggedIn;
      if (typeof isLoggedInOverride !== 'undefined') {
        logged = isLoggedInOverride;
      }

      if (!logged) {
        // TODO: Set session to resume process after authentication
        setStorage('_afterAuth', JSON.stringify({ action: 'saveWishlist', name }), true);
        console.log('User not logged in :(');
        relocate(`/login/`);
        res(true);
        return;
      }

      // Enter products into new wishlist
      const items = sessionWishlistIds.map(product => {
        return {
          productId: parseInt(product[0], 10),
          variantId: parseInt(product[1], 10)
        };
      });

      const wishlistsBody = {
        name,
        items
      };

      railwayApi('v1/customer/wishlists', 'POST', wishlistsBody).then(response => {
        // Running manually as saveSessionWishlistIds isn't updating the session for some reason
        clearSessionWishlist();
        setStorage('sessionWishlistIds', JSON.stringify([]));
        res(response);
        return;
      });
    });
  };

  const getAccountWishlists = async () => {
    const res = await railwayApi('v1/customer/wishlists');
    if (!res || !res.data) {
      return [];
    }
    setExistingWishlists(res.data.map(item => item.name));
    return res;
  };

  const getWishlistItems = (items, width) => {
    const productIds = [];
    const variantIds = [];
    items.map(ids => {
      if (ids.productId) {
        productIds.push(ids.productId);
      }
      if (ids.variantId) {
        variantIds.push(ids.variantId);
      }
      return true;
    });
    return graphqlQuery(query, { productIds, variantIds, width })
      .then(response => {
        if (response.data && response.data.site.products.edges.length > 0) {
          /* Extend on product data here if needed - for example, fetch additional data from a PIM and add it to the product object */
          return response.data.site.products.edges;
        } else {
          return [];
        }
      })
      .catch(error => {
        console.log('fetch list error:', error);
        return error;
      });
  };

  const removeWishlist = async wishlistId => {
    const res = await railwayApi(`v1/customer/wishlists/${wishlistId}`, 'DELETE');
    if (res.error) {
      toast({
        id: 'wishlists',
        title: 'Delete wishlist falied!',
        description: 'Please refresh the page and try again.',
        status: 'error',
        position: 'top-right'
      });
      return;
    }

    return;
  };

  const removeFromAccountWishlist = (
    wishlistId,
    wishlistItems,
    wishlistItemId,
    productId,
    variantId
  ) => {
    return new Promise((res, rej) => {
      if (!wishlistId) rej('Missing wishlist ID');

      if (wishlistItemId) {
        // Use wishlist item ID to remove
        railwayApi(`v1/customer/wishlists/${wishlistId}/items/${wishlistItemId}`, 'DELETE').then(
          respone => {
            res(respone);
          }
        );
      } else if (productId && variantId) {
        // Use the product ID and variant ID to find the wishlist item in the wishlist
        let _wishlistItems = wishlistItems;
        const promises = [];
        if (!wishlistItems) {
          promises.push(
            new Promise((wlres, wlrej) => {
              railwayApi('v1/customer/wishlists').then(response => {
                if (response?.data) {
                  _wishlistItems = response.data.find(wl => wl.id === wishlistId)?.items;
                  wlres(true);
                } else {
                  wlrej(false);
                }
              });
            })
          );
        }

        let _wishlistItemId = -1;
        Promise.all(promises).then(() => {
          if (_wishlistItems) {
            let deleteIndex = -1;
            _wishlistItems.map((item, itemIndex) => {
              if (item.productId === productId && item.variantId === variantId) {
                _wishlistItemId = item.id;
                deleteIndex = itemIndex;
              }

              return true;
            });

            if (deleteIndex > -1) {
              _wishlistItems.splice(deleteIndex, 1);
            }
          }

          if (_wishlistItemId > 0) {
            railwayApi(
              `v1/customer/wishlists/${wishlistId}/items/${_wishlistItemId}`,
              'DELETE'
            ).then(response => {
              if (!response.error) {
                res(_wishlistItems);
              } else rej(response);
            });
          }
        });
      }
    });
  };

  const addItemToWishlist = (wishlistId, productId, variantId) => {
    return new Promise((res, rej) => {
      railwayApi(`v1/customer/wishlists/${wishlistId}/items`, 'POST', {
        items: [
          {
            product_id: productId,
            variant_id: variantId
          }
        ]
      }).then(response => {
        const added = response.data.items.find(
          item => item.productId === productId && item.variantId === variantId
        );
        if (added) {
          res(response);
        } else rej(response);
      });
    });
  };

  const checkNameDuplicate = name => {
    try {
      const normalizedName = name.trim().replace(/\s+/g, '').toLowerCase();
      if (existingWishlists.length) {
        const trimmedWishlist = existingWishlists.map(nameItem => {
          return nameItem.trim().replace(/\s+/g, '').toLowerCase();
        });
        if (trimmedWishlist.includes(normalizedName)) {
          return false;
        }
      }
      return true;
    } catch {
      return true;
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        sessionWishlistIds,
        sessionWishlistData,
        updatingId,
        updateSessionWishlist,
        clearSessionWishlist,
        existsInWishlist,
        saveWishlist,
        getAccountWishlists,
        getWishlistItems,
        removeFromAccountWishlist,
        removeWishlist,
        checkNameDuplicate,
        setExistingWishlists,
        addItemToWishlist
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
